<template>
  <OverlayFrame
    :zIndex="overlayZIndex"
    :title="title"
    :subtitle="subtitle"
    :icon="ICONS.actions.spendAp"
    closeLabel="Close spend AP"
    :style="{ '--accent': 'var(--accent-work)' }"
    wide
    @close="emit('close')"
  >
    <div class="stack tight">
      <UiPanel tone="work">
        <UiSectionHeader>
          <template #title>Allocate</template>
          <template #actions>
            <UiChip variant="muted" class="stat-mono num">{{ formatNumber(remainingAp) }} AP left</UiChip>
          </template>
        </UiSectionHeader>

        <div class="grid">
          <div v-for="row in rows" :key="row.key" class="attr">
            <div class="attr-head">
              <div class="attr-label">{{ row.label }}</div>
              <UiChip v-if="row.inc > 0" variant="active" class="stat-mono num">+{{ formatNumber(row.inc) }}</UiChip>
            </div>
            <div class="attr-meta text-muted stat-mono num">
              {{ formatNumber(row.base) }} → {{ formatNumber(row.base + row.inc) }}
            </div>
            <UiQuantity v-model="increments[row.key]" :min="0" :max="maxFor(row.key)" :disabled="totalAp <= 0" :showMax="false" />
          </div>
        </div>

        <div class="row wrap" style="justify-content: flex-end; margin-top: 0.85rem;">
          <UiButton size="sm" variant="secondary" :disabled="spentAp === 0" @click="reset">
            <template #icon>{{ ICONS.actions.trash }}</template>
            Reset
          </UiButton>
          <UiButton size="sm" tone="work" :disabled="spentAp === 0" @click="submit">
            <template #icon>{{ ICONS.states.active }}</template>
            Spend <span class="stat-mono num">{{ formatNumber(spentAp) }}</span>
          </UiButton>
        </div>
      </UiPanel>

      <UiCallout v-if="totalAp <= 0" variant="info">No AP available.</UiCallout>
    </div>
  </OverlayFrame>
</template>

<script setup lang="ts">
import { computed, reactive } from "vue";
import type { Attributes } from "@idle-rpg/shared";
import { ICONS } from "../../../lib/icons";
import { formatNumber } from "../../../lib/format";
import OverlayFrame from "../../../components/overlay/OverlayFrame.vue";
import UiButton from "../../../components/ui/UiButton.vue";
import UiCallout from "../../../components/ui/UiCallout.vue";
import UiChip from "../../../components/ui/UiChip.vue";
import UiPanel from "../../../components/ui/UiPanel.vue";
import UiQuantity from "../../../components/ui/UiQuantity.vue";
import UiSectionHeader from "../../../components/ui/UiSectionHeader.vue";

const props = withDefaults(
  defineProps<{
    overlayZIndex?: number;
    currentAttributes: Attributes;
    totalAp: number;
    onSpend?: (increments: Record<string, number>) => Promise<void> | void;
  }>(),
  {
    overlayZIndex: 4000,
    onSpend: undefined
  }
);

const emit = defineEmits<{ close: [] }>();

const increments = reactive<Record<keyof Attributes, number>>({
  str: 0,
  dex: 0,
  con: 0,
  int: 0,
  wis: 0,
  cha: 0
});

const rows = computed(() => {
  const base = props.currentAttributes;
  return (Object.keys(increments) as Array<keyof Attributes>).map((key) => ({
    key,
    label: String(key).toUpperCase(),
    base: Math.floor(Number(base[key] ?? 0)),
    inc: Math.floor(Number(increments[key] ?? 0))
  }));
});

const spentAp = computed(() => rows.value.reduce((sum, r) => sum + Math.max(0, r.inc), 0));
const remainingAp = computed(() => Math.max(0, Math.floor(props.totalAp) - spentAp.value));

const title = computed(() => "Spend AP");
const subtitle = computed(() => "Allocate AP across attributes. You can spend partially.");

function maxFor(key: keyof Attributes) {
  const current = Math.max(0, Math.floor(increments[key] ?? 0));
  return current + remainingAp.value;
}

function reset() {
  for (const key of Object.keys(increments) as Array<keyof Attributes>) increments[key] = 0;
}

async function submit() {
  if (spentAp.value <= 0) return;
  await props.onSpend?.(increments);
  emit("close");
}
</script>

<style scoped>
.grid {
  margin-top: var(--space-sm);
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.attr {
  border: 1px solid var(--glass-border-0);
  background: var(--glass-0);
  border-radius: var(--radius-2);
  padding: 0.75rem;
  display: grid;
  gap: 0.55rem;
}

.attr-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.attr-label {
  font-weight: 900;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.attr-meta {
  font-size: 0.85rem;
}

@media (max-width: 720px) {
  .grid {
    grid-template-columns: 1fr;
  }
}
</style>

